/** First-party YouTube time-range clips: /clip/{id} */

export const CLIP_TITLE_MAX = 100;
export const CLIP_MAX_SECONDS = 60;
export const CLIP_ARM_MS = 60000;
export const CLIP_CREATED_EVENT = 'bp:clip-created';
export const CLIP_DELETED_EVENT = 'bp:clip-deleted';

/** YouTube live archives max out around 11 hours 55 minutes; the rest is cut. */
export const YT_LIVE_ARCHIVE_MAX_SECONDS = 11 * 3600 + 55 * 60; // 42900

export function isYoutubeLiveArchiveCapped(durationSeconds) {
  const d = Number(durationSeconds);
  return Number.isFinite(d) && d >= YT_LIVE_ARCHIVE_MAX_SECONDS - 30;
}

/**
 * Live-head clocks (getCurrentTime === 0, duration = DVR) still happen when
 * video.liveStatus has already flipped to "ended". Latch any strong live signal,
 * including a playhead that stays at 0 while the video is playing.
 */
export function inferClipIsLive({
  isLive = false,
  looksLive = false,
  duration = null,
  previousDuration = null,
  position = null,
  playerState = null,
  wasStuckAtHead = false
} = {}) {
  if (isLive || looksLive) return true;
  const d = Number(duration);
  const prev = Number(previousDuration);
  if (
    previousDuration != null
    && Number.isFinite(d)
    && Number.isFinite(prev)
    && prev > 0
    && d > prev + 0.4
  ) {
    return true;
  }
  const t = Number(position);
  const playing = playerState === 1 || playerState === 3;
  if (
    wasStuckAtHead
    && playing
    && position != null
    && Number.isFinite(t)
    && t < 1
    && Number.isFinite(d)
    && d > 1
  ) {
    return true;
  }
  return false;
}

/** False when a clip starts at/after YouTube's truncated live archive. */
export function isClipInsideYoutubeArchive(startSeconds) {
  const start = Math.floor(Number(startSeconds));
  return Number.isFinite(start) && start >= 0 && start < YT_LIVE_ARCHIVE_MAX_SECONDS;
}

const CLIP_PATH_RE = /^(?:\/legacy)?\/clip\/([^/?#]+)\/?$/i;

export function parseClipHref(href) {
  if (!href) return null;
  let url;
  try {
    url = new URL(href, typeof window !== 'undefined' ? window.location.origin : 'https://bigplays.gg');
  } catch {
    return null;
  }
  const pathMatch = url.pathname.replace(/\/+$/, '').match(CLIP_PATH_RE);
  if (!pathMatch) return null;
  let id;
  try {
    id = decodeURIComponent(pathMatch[1]);
  } catch {
    id = pathMatch[1];
  }
  if (!id || !/^\d+$/.test(id)) return null;
  return { clipId: id };
}

export function buildClipPath(clipId) {
  return `/clip/${encodeURIComponent(String(clipId))}`;
}

export function buildClipUrl(clipId) {
  const origin = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'https://bigplays.gg';
  return `${origin}${buildClipPath(clipId)}`;
}

/** Put a chat-opened clip first so Next starts at the most recent remaining clip. */
export function clipsFeedWithLinkedFirst(clips, linkedClip) {
  const list = Array.isArray(clips) ? clips.slice() : [];
  if (!linkedClip || linkedClip.clipId == null || linkedClip.clipId === '') return list;
  const id = String(linkedClip.clipId);
  return [linkedClip, ...list.filter((clip) => String(clip.clipId) !== id)];
}

export function clampClipRange(startSeconds, endSeconds, maxSeconds = CLIP_MAX_SECONDS) {
  const start = Math.max(0, Math.floor(Number(startSeconds) || 0));
  let end = Math.floor(Number(endSeconds) || 0);
  if (!(end > start)) return null;
  const cap = Math.max(1, Math.floor(Number(maxSeconds) || CLIP_MAX_SECONDS));
  if (end - start > cap) end = start + cap;
  if (end - start < 1) return null;
  return { startSeconds: start, endSeconds: end };
}

/**
 * Build a clip range from two clock taps.
 * At live edge YouTube's playhead often sits on (or just past) duration and
 * does not advance — wall-clock elapsed is then the clip length ending now.
 */
export function clipRangeFromClockTaps(startSeconds, endSeconds, {
  elapsedSeconds = 0,
  isLive = false,
  durationSeconds = null,
  maxSeconds = CLIP_MAX_SECONDS
} = {}) {
  const elapsed = Math.max(0, Math.floor(Number(elapsedSeconds) || 0));
  const cap = Math.max(1, Math.floor(Number(maxSeconds) || CLIP_MAX_SECONDS));
  let start = Math.max(0, Math.floor(Number(startSeconds) || 0));
  let end = Math.floor(Number(endSeconds) || 0);
  const d = Number(durationSeconds);
  const duration = (Number.isFinite(d) && d > 0 && d !== Infinity) ? Math.floor(d) : null;

  // Live head often reports 0; duration is the DVR clock in that case.
  if (isLive && duration != null) {
    if (start < 1) start = duration;
    if (end < 1) end = duration;
  }

  const direct = clampClipRange(start, end, cap);
  if (direct) return direct;

  // Player clock didn't move later: live head stuck, duration vs watch-offset
  // reset (0→duration on tap 1, then 30s later getCurrentTime is 30), or a
  // capped DVR. The time between taps is the clip length.
  if (elapsed >= 1) {
    return clampClipRange(start, start + elapsed, cap);
  }
  return null;
}

/**
 * Pause a clip only after playback has actually entered the range.
 * Live players often start at the live edge (far past end); pausing then
 * would freeze the clip before the DVR seek lands.
 */
export function clipPauseDecision(position, startSeconds, endSeconds, hasEnteredClip) {
  const t = Number(position);
  const start = Number(startSeconds);
  const end = Number(endSeconds);
  if (!Number.isFinite(t) || !Number.isFinite(start) || !Number.isFinite(end) || !(end > start)) {
    return { entered: !!hasEnteredClip, pause: false };
  }
  if (t >= start - 1 && t < end - 0.2) {
    return { entered: true, pause: false };
  }
  if (!hasEnteredClip) {
    return { entered: false, pause: false };
  }
  return { entered: true, pause: t >= end - 0.2 };
}

/**
 * Restart a finished clip at its start. YouTube's native restart control jumps
 * to 0 of the parent VOD, so we seek and play ourselves.
 */
export function replayClipFromStart(player, startSeconds) {
  if (!player) return false;
  const start = Math.max(0, Math.floor(Number(startSeconds) || 0));
  try {
    if (typeof player.seekTo === 'function') player.seekTo(start, true);
    if (typeof player.playVideo === 'function') player.playVideo();
    return true;
  } catch (_) {
    return false;
  }
}

/** YouTube IFrame PlayerState values we treat as a usable clock. */
const CLIPABLE_PLAYER_STATES = new Set([
  1, // PLAYING
  2, // PAUSED
  3  // BUFFERING (keep a valid DVR time through a hitch)
]);

/**
 * Whether this player time is safe to use as a clip bound.
 * VODs: any finite t >= 0.
 * Lives: need a DVR-like offset (t >= 1) inside a seekable window. Duration 0/NaN
 * means no rewind; time past duration means we are not in the archive — except
 * when YouTube has capped duration at ~11:55:00 and the playhead sits on/over it.
 */
export function clipablePlayerTime({
  position,
  duration,
  playerState,
  isLive = false
} = {}) {
  const t = Math.floor(Number(position));
  if (!Number.isFinite(t) || t < 0) return null;
  if (!isLive) return t;

  if (t < 1) return null;
  if (playerState != null && !CLIPABLE_PLAYER_STATES.has(playerState)) return null;

  const d = Number(duration);
  if (d === Infinity) return t;
  if (!Number.isFinite(d) || d <= 0) return null;
  // Live edge often reads past getDuration(), including on streams that are
  // nowhere near YouTube's 11:55:00 archive cap.
  if (t > d + 2) {
    if (t <= d + 30) return t;
    return null;
  }
  return t;
}

/**
 * Live players often report getCurrentTime() === 0 at the live head.
 * Duration is the DVR clock in that case — use it as the clip/timestamp time.
 */
export function resolveClipClock({
  position,
  duration,
  playerState,
  isLive = false
} = {}) {
  let t = Math.floor(Number(position));
  if (!Number.isFinite(t) || t < 0) t = null;
  const d = Number(duration);
  const hasDuration = Number.isFinite(d) && d > 1 && d !== Infinity;
  const stateOk = playerState == null || CLIPABLE_PLAYER_STATES.has(playerState);

  if (isLive && stateOk && hasDuration && (t == null || t < 1)) {
    t = Math.floor(d);
  }

  return {
    seconds: clipablePlayerTime({
      position: t,
      duration,
      playerState,
      isLive
    }),
    position: t,
    duration: hasDuration ? d : duration
  };
}

/**
 * Map a stored clip onto the player's current duration.
 * Lives keep the stored range unless the start is past a known DVR window.
 * VODs clamp end to duration; start past duration is unplayable (truncated archive).
 */
export function clipRangeForPlayerDuration(
  startSeconds,
  endSeconds,
  durationSeconds,
  { isLive = false } = {}
) {
  const start = Math.max(0, Math.floor(Number(startSeconds) || 0));
  let end = Math.floor(Number(endSeconds) || 0);
  if (!(end > start)) return { playable: false, reason: 'invalid' };

  const d = Number(durationSeconds);
  const hasDuration = Number.isFinite(d) && d > 0 && d !== Infinity;
  if (!hasDuration) {
    return { playable: true, startSeconds: start, endSeconds: end };
  }

  const duration = Math.floor(d);
  if (start >= duration) {
    if (isLive && isYoutubeLiveArchiveCapped(duration)) {
      const span = Math.min(Math.max(1, end - start), CLIP_MAX_SECONDS);
      const clampedStart = Math.max(0, duration - span);
      return { playable: true, startSeconds: clampedStart, endSeconds: duration };
    }
    if (isLive) {
      return { playable: false, reason: 'not-in-dvr' };
    }
    return { playable: false, reason: 'truncated' };
  }

  end = Math.min(end, duration);
  if (!(end > start)) return { playable: false, reason: 'truncated' };
  return { playable: true, startSeconds: start, endSeconds: end };
}

const titleCache = Object.create(null);
const titleInflight = Object.create(null);
const deletedClips = Object.create(null);

export function markClipDeleted(id) {
  if (id == null || id === '') return;
  const key = String(id);
  deletedClips[key] = true;
  delete titleCache[key];
}

export function isClipDeleted(id) {
  if (id == null || id === '') return false;
  return !!deletedClips[String(id)];
}

export function rememberClipTitle(id, title) {
  if (id == null || isClipDeleted(id)) return;
  const key = String(id);
  const name = String(title || '').trim();
  if (name) titleCache[key] = name;
}

export function getCachedClipTitle(id) {
  if (id == null || isClipDeleted(id)) return '';
  return titleCache[String(id)] || '';
}

export function fetchClipTitle(id, { revalidate = false } = {}) {
  if (id == null || id === '') return Promise.resolve('');
  const key = String(id);
  if (isClipDeleted(key)) return Promise.resolve('');
  if (!revalidate && Object.prototype.hasOwnProperty.call(titleCache, key)) {
    return Promise.resolve(titleCache[key]);
  }
  if (titleInflight[key]) return titleInflight[key];
  titleInflight[key] = fetch(`/api/clips/${encodeURIComponent(key)}`, {
    credentials: 'include'
  })
    .then((r) => {
      if (r.status === 404) {
        markClipDeleted(key);
        return null;
      }
      return r.ok ? r.json() : null;
    })
    .then((data) => {
      if (isClipDeleted(key)) return '';
      const name = String(data?.title || '').trim();
      titleCache[key] = name;
      if (data?.clipId != null) rememberClipTitle(data.clipId, name);
      return name;
    })
    .catch(() => {
      if (isClipDeleted(key)) return '';
      return '';
    })
    .finally(() => {
      delete titleInflight[key];
    });
  return titleInflight[key];
}

if (typeof window !== 'undefined' && !window.__bpClipDeletedBound) {
  window.__bpClipDeletedBound = true;
  window.addEventListener(CLIP_DELETED_EVENT, (e) => {
    markClipDeleted(e.detail?.clipId);
  });
}
